/**
 * O botão "Exportar" das telas de listagem — CSV e PDF, num menu só.
 *
 * Compartilhado por `TelaClientes`, `TelaFornecedores` e `TelaProdutos`
 * porque as três fazem exatamente a mesma coisa com colunas diferentes:
 * baixar um CSV e montar uma view impressa. Escrever a lógica três vezes
 * teria dado três formatos de data no nome do arquivo na primeira vez que
 * alguém mexesse numa cópia e esquecesse as outras duas.
 *
 * ## Por que exporta a lista inteira, e não só a página
 *
 * Nenhuma das três listagens pagina no cliente: `carregarClientes`,
 * `carregarFornecedores` e `carregarCatalogo` devolvem TODAS as linhas que
 * batem com a busca e o filtro atuais numa chamada só (o servidor tem um teto
 * de segurança — 200 linhas em clientes e fornecedores — mas não uma
 * "página"). Ou seja: `linhas` que a tela já carregou É o filtro inteiro, e
 * exportar exatamente isso é exportar o que a pessoa pediu, sem chamada
 * adicional. Ver `docs/de-para.md` para o registro desta decisão.
 *
 * ## Por que PDF é impressão do navegador, e não uma lib
 *
 * Ver o comentário no topo de `exportacao.ts`.
 *
 * ## O menu
 *
 * Mesma estrutura ARIA e o mesmo truque de posicionamento por `createPortal`
 * que `MenuDeLinha` (`componentes/Listagem.tsx`) e `MenuAcoesLinha` (dentro
 * das telas de listagem): portal para o `<body>`, posição `fixed` medida a
 * partir do botão. Sem o portal, o menu fica cortado pelo cartão com
 * `overflow-hidden` que envolve a tabela — foi o mesmo bug, resolvido do
 * mesmo jeito.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'

import { useSessao } from '../plataforma/contexto-sessao'
import { baixarCSV, type ColunaExportacao } from './exportacao'
import { Icone } from './Icone'

/**
 * Id fixo da área de impressão. Só uma tela de listagem está montada por
 * vez (é uma rota), então não há risco de duas colidirem no mesmo `<body>`.
 *
 * É exportado porque a folha de etiquetas (`modulos/etiquetas`) imprime pelo
 * mesmo mecanismo e precisa do MESMO id — a regra do `tokens.css` procura
 * este elemento para esconder o casco do app, e uma segunda string mágica
 * igual em outro arquivo é a que fica para trás quando esta mudar.
 *
 * A regra de impressão em `tokens.css` (`body:has(#${ID_AREA_DE_IMPRESSAO})`)
 * usa este mesmo id para esconder o casco do app (barra lateral, topo) SÓ
 * nas telas que têm exportação — as demais imprimem do jeito que o navegador
 * imprimiria sem nenhuma folha de estilo dedicada, porque não têm o elemento
 * que a regra procura.
 */
export const ID_AREA_DE_IMPRESSAO = 'area-de-impressao-lumma'

export type PropsBotaoExportar<T> = {
  /** "clientes" → nome do arquivo `clientes-2026-09-07.csv`. */
  nomeBase: string
  /** Título do relatório impresso — "Clientes", "Catálogo de produtos". */
  titulo: string
  /** Frase de uma linha com o recorte ativo. Ausente quando não há filtro. */
  filtroAplicado?: string
  colunas: ColunaExportacao<T>[]
  linhas: T[]
}

export function BotaoExportar<T>({
  nomeBase,
  titulo,
  filtroAplicado,
  colunas,
  linhas,
}: PropsBotaoExportar<T>) {
  const [aberto, setAberto] = useState(false)
  const [posicao, setPosicao] = useState({ top: 0, right: 0 })
  const [imprimindo, setImprimindo] = useState(false)
  const botaoRef = useRef<HTMLButtonElement>(null)

  // A view impressa só existe enquanto dura a impressão. Ficar sempre
  // montada (mesmo invisível em tela) duplicaria código, nome e telefone no
  // DOM em toda tela que usa este botão — inofensivo para o navegador, mas
  // suficiente para `getByRole('table')` e `getByText(...)` de um teste
  // pararem de saber qual dos dois nós é "a" tabela.
  useEffect(() => {
    if (!imprimindo) return
    const aoTerminar = () => setImprimindo(false)
    window.addEventListener('afterprint', aoTerminar)
    return () => window.removeEventListener('afterprint', aoTerminar)
  }, [imprimindo])

  const abrir = () => {
    const retangulo = botaoRef.current?.getBoundingClientRect()
    if (retangulo) {
      setPosicao({ top: retangulo.bottom + 4, right: window.innerWidth - retangulo.right })
    }
    setAberto(true)
  }

  // Mesmo comportamento do menu ⋮ da linha: rolar com o menu aberto deixaria
  // o popover flutuando longe do botão, e Esc é o atalho de teclado padrão
  // para fechar um menu.
  useEffect(() => {
    if (!aberto) return
    const fechar = () => setAberto(false)
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false)
    }
    window.addEventListener('scroll', fechar, true)
    window.addEventListener('resize', fechar)
    document.addEventListener('keydown', aoTeclar)
    return () => {
      window.removeEventListener('scroll', fechar, true)
      window.removeEventListener('resize', fechar)
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [aberto])

  const exportarCSV = () => {
    setAberto(false)
    baixarCSV(nomeBase, colunas, linhas)
  }

  const exportarPDF = () => {
    setAberto(false)
    // `flushSync` aplica a montagem da view impressa AGORA, antes de
    // `window.print()` ler o DOM: `print()` é síncrono do ponto de vista do
    // JavaScript, e não espera o próximo commit do React — sem isto, o
    // diálogo de impressão abriria com o estado de ANTES deste clique.
    flushSync(() => setImprimindo(true))
    window.print()
  }

  return (
    <>
      <button
        ref={botaoRef}
        type="button"
        onClick={() => (aberto ? setAberto(false) : abrir())}
        aria-haspopup="menu"
        aria-expanded={aberto}
        className="flex h-[34px] items-center gap-2 rounded-sistema border border-borda bg-superficie px-3.5 text-[13.5px] font-semibold text-texto hover:border-inativo"
      >
        <Icone nome="baixar" tamanho={18} />
        Exportar
      </button>

      {aberto &&
        createPortal(
          <>
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              onClick={() => setAberto(false)}
              className="fixed inset-0 z-40 cursor-default"
            />
            <div
              role="menu"
              aria-label="Exportar"
              style={{ top: posicao.top, right: posicao.right }}
              className="fixed z-50 w-[190px] rounded-sistema border border-borda bg-superficie py-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
            >
              <button
                type="button"
                role="menuitem"
                onClick={exportarCSV}
                className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13.5px] font-medium text-texto hover:bg-divisoria"
              >
                <Icone nome="grid_view" tamanho={17} />
                Exportar CSV
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={exportarPDF}
                className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13.5px] font-medium text-texto hover:bg-divisoria"
              >
                <Icone nome="imprimir" tamanho={17} />
                Exportar PDF
              </button>
            </div>
          </>,
          document.body,
        )}

      {imprimindo &&
        createPortal(
          <RelatorioImpressao
            titulo={titulo}
            filtroAplicado={filtroAplicado}
            colunas={colunas}
            linhas={linhas}
          />,
          document.body,
        )}
    </>
  )
}

/**
 * A view "PDF" — invisível na tela (`hidden`), e é tudo o que aparece na
 * impressão (`print:block`), porque `tokens.css` esconde o resto do app
 * quando este elemento existe no `<body>`. Só existe enquanto `imprimindo`
 * for `true` — ver o comentário no topo de `BotaoExportar`.
 */
function RelatorioImpressao<T>({
  titulo,
  filtroAplicado,
  colunas,
  linhas,
}: {
  titulo: string
  filtroAplicado?: string
  colunas: ColunaExportacao<T>[]
  linhas: T[]
}) {
  const { situacao } = useSessao()
  const loja = situacao.estado === 'autenticado' ? situacao.sessao.loja : null
  const nomeDaLoja = loja ? loja.nome_fantasia || loja.nome : ''

  return (
    <div id={ID_AREA_DE_IMPRESSAO} className="hidden p-8 text-tinta print:block">
      <header className="mb-5 flex items-start justify-between gap-4 border-b-2 border-tinta pb-3">
        <div>
          <p className="text-base font-bold">{nomeDaLoja}</p>
          <h1 className="text-sm font-semibold text-texto">{titulo}</h1>
          {filtroAplicado && <p className="mt-1 text-xs text-texto-suave">{filtroAplicado}</p>}
        </div>
        <p className="tabular shrink-0 text-xs text-texto-suave">
          Gerado em {new Date().toLocaleString('pt-BR')}
        </p>
      </header>

      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {colunas.map((coluna) => (
              <th
                key={coluna.cabecalho}
                className={`border-b border-tinta py-1.5 pr-3 font-semibold ${
                  coluna.alinhamento === 'direita' ? 'text-right' : 'text-left'
                }`}
              >
                {coluna.cabecalho}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha, indice) => (
            // Índice como chave: a linha do relatório não tem identidade
            // própria neste ponto (algumas colunas nem trazem "código"), e a
            // ordem não muda durante a vida deste componente.
            <tr key={indice} style={{ breakInside: 'avoid' }}>
              {colunas.map((coluna) => (
                <td
                  key={coluna.cabecalho}
                  className={`border-b border-divisoria py-1 pr-3 ${
                    coluna.alinhamento === 'direita' ? 'text-right' : 'text-left'
                  }`}
                >
                  {coluna.valor(linha)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <footer className="tabular mt-3 text-xs text-texto-suave">
        {linhas.length} {linhas.length === 1 ? 'registro' : 'registros'}
      </footer>
    </div>
  )
}
